export function getApiBaseUrl(): string {
  if (import.meta.env.VITE_API_URL) return import.meta.env.VITE_API_URL
  if (import.meta.env.VITE_API_BASE_URL) return import.meta.env.VITE_API_BASE_URL
  const env = import.meta.env.VITE_APP_ENV || import.meta.env.MODE
  if (env === 'staging') return 'https://api-staging.oltier.com'
  if (env === 'production') return 'https://api.oltier.com'
  return 'http://localhost:8080'
}

const BASE = getApiBaseUrl()
const PROJECT_KEY = 'icc'

interface ApiResponse<T> {
  success: boolean
  statusCode: number
  message: string
  data: T
}

function clearAuth() {
  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('token_expires_in')
  localStorage.removeItem('token_type')
  localStorage.removeItem('userId')
  localStorage.removeItem('userEmail')
  localStorage.removeItem('projectKey')
  localStorage.removeItem('token_timestamp')
}

async function req<T>(endpoint: string, opts: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('accessToken')
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Project-Key': PROJECT_KEY,
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(opts.headers as Record<string, string>),
  }

  const res = await fetch(`${BASE}${endpoint}`, { ...opts, headers })

  if (res.status === 401) {
    clearAuth()
    window.location.href = '/'
    throw new Error('Session expired')
  }

  const json: ApiResponse<T> = await res.json()

  if (res.status === 403) {
    const j = json as any
    const msg = typeof j?.message === 'string' ? j.message.toLowerCase() : ''
    if (j?.subscriptionRequired || msg.includes('subscription') || msg.includes('authentication required')) {
      throw new Error('SUBSCRIPTION_REQUIRED')
    }
  }

  if (!json.success) throw new Error(json.message || 'Request failed')
  return json.data
}

/** Public request — no auth token, no 401 redirect */
async function pubReq<T>(endpoint: string): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Project-Key': PROJECT_KEY,
  }
  const res = await fetch(`${BASE}${endpoint}`, { headers })
  const json: ApiResponse<T> = await res.json()
  if (!json.success) throw new Error(json.message || 'Request failed')
  return json.data
}

// ── Public Content APIs ──

export const api = {
  // Courses
  getCourses: () => req<unknown[]>('/api/icc/courses'),
  getCourse: (slug: string) => req<unknown>(`/api/icc/courses/${slug}`),
  getModule: (courseSlug: string, moduleSlug: string) =>
    req<unknown>(`/api/icc/courses/${courseSlug}/modules/${moduleSlug}`),
  getQuiz: (courseSlug: string, moduleSlug: string) =>
    req<unknown>(`/api/icc/courses/${courseSlug}/modules/${moduleSlug}/quiz`),

  // Content
  getGlossary: () => req<unknown[]>('/api/icc/glossary'),
  getResources: () => req<unknown[]>('/api/icc/resources'),

  // Progress (authenticated)
  getProgress: () => req<unknown[]>('/api/icc/me/progress'),
  getCourseProgress: (courseSlug: string) =>
    req<unknown>(`/api/icc/me/progress/courses/${courseSlug}`),
  updateProgress: (data: unknown) =>
    req<unknown>('/api/icc/me/progress', { method: 'POST', body: JSON.stringify(data) }),

  // Stats
  getStats: () => req<unknown>('/api/icc/me/stats'),

  // Quizzes
  submitQuizAttempt: (quizId: string, data: unknown) =>
    req<unknown>(`/api/icc/quizzes/${quizId}/attempt`, { method: 'POST', body: JSON.stringify(data) }),
  getQuizAttempts: () => req<unknown[]>('/api/icc/me/quiz-attempts'),

  // Bookmarks
  getBookmarks: () => req<unknown[]>('/api/icc/me/bookmarks'),
  addBookmark: (moduleId: string) =>
    req<unknown>(`/api/icc/me/bookmarks/${moduleId}`, { method: 'POST' }),
  removeBookmark: (moduleId: string) =>
    req<unknown>(`/api/icc/me/bookmarks/${moduleId}`, { method: 'DELETE' }),

  // Notes
  getNotes: () => req<unknown[]>('/api/icc/me/notes'),
  getNotesByModule: (moduleId: string) =>
    req<unknown[]>(`/api/icc/me/notes/module/${moduleId}`),
  createNote: (data: { moduleId: string; content: string }) =>
    req<unknown>('/api/icc/me/notes', { method: 'POST', body: JSON.stringify(data) }),
  updateNote: (id: string, content: string) =>
    req<unknown>(`/api/icc/me/notes/${id}`, { method: 'PUT', body: JSON.stringify({ content }) }),
  deleteNote: (id: string) =>
    req<unknown>(`/api/icc/me/notes/${id}`, { method: 'DELETE' }),

  // Achievements
  getAchievements: () => req<unknown[]>('/api/icc/me/achievements'),

  // Enrollments
  getEnrollments: () => req<unknown[]>('/api/icc/me/enrollments'),
  enroll: (courseSlug: string) =>
    req<unknown>(`/api/icc/me/enrollments/${courseSlug}`, { method: 'POST' }),
  unenroll: (courseSlug: string) =>
    req<unknown>(`/api/icc/me/enrollments/${courseSlug}`, { method: 'DELETE' }),

  // Subscription
  getSubscriptionPlans: () => pubReq<unknown[]>('/api/icc/subscription/plans'),
  getSubscriptionStatus: () => req<unknown>('/api/icc/subscription/me/status'),
  subscribe: (
    planSlug: string,
    returnUrl?: string,
    phoneNumber?: string,
    paymentMode?: string,
    referralCode?: string,
  ) =>
    req<unknown>('/api/icc/subscription/me/subscribe', {
      method: 'POST',
      body: JSON.stringify({
        planSlug,
        returnUrl,
        phoneNumber,
        paymentMode: paymentMode || 'one_time',
        // Only sent when present so non-referral flows remain byte-identical.
        ...(referralCode ? { referralCode } : {}),
      }),
    }),
  verifyPayment: (id: string) =>
    req<unknown>(`/api/icc/subscription/me/verify-payment?${id.startsWith('icc_sub_') ? 'subscription_id' : 'order_id'}=${id}`),
  cancelSubscription: () =>
    req<unknown>('/api/icc/subscription/me/cancel', { method: 'POST' }),
  getPaymentHistory: () => req<unknown[]>('/api/icc/subscription/me/payments'),
  downloadReceipt: async (paymentId: string) => {
    const token = localStorage.getItem('accessToken')
    const headers: Record<string, string> = {
      'X-Project-Key': 'icc',
      ...(token && { Authorization: `Bearer ${token}` }),
    }
    const base = getApiBaseUrl()
    const res = await fetch(`${base}/api/icc/subscription/me/payments/${paymentId}/receipt`, { headers })
    if (!res.ok) throw new Error('Failed to download receipt')
    return res.blob()
  },

  // ── Admin (server gates on admin email) ──
  adminWhoami: () => req<{ isAdmin: boolean }>('/api/icc/admin/whoami'),
  adminListCourses: () => req<unknown[]>('/api/icc/admin/courses'),
  adminCreateCourse: (data: unknown) =>
    req<unknown>('/api/icc/admin/courses', { method: 'POST', body: JSON.stringify(data) }),
  adminUpdateCourse: (slug: string, data: unknown) =>
    req<unknown>(`/api/icc/admin/courses/${slug}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  adminDeleteCourse: (slug: string) =>
    req<unknown>(`/api/icc/admin/courses/${slug}`, { method: 'DELETE' }),
  adminListModules: (courseSlug: string) =>
    req<unknown[]>(`/api/icc/admin/courses/${courseSlug}/modules`),
  adminCreateModule: (courseSlug: string, data: unknown) =>
    req<unknown>(`/api/icc/admin/courses/${courseSlug}/modules`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  adminUpdateModule: (courseSlug: string, moduleSlug: string, data: unknown) =>
    req<unknown>(
      `/api/icc/admin/courses/${courseSlug}/modules/${moduleSlug}`,
      { method: 'PUT', body: JSON.stringify(data) },
    ),
  adminDeleteModule: (courseSlug: string, moduleSlug: string) =>
    req<unknown>(
      `/api/icc/admin/courses/${courseSlug}/modules/${moduleSlug}`,
      { method: 'DELETE' },
    ),
  adminUpsertQuiz: (courseSlug: string, moduleSlug: string, data: unknown) =>
    req<unknown>(
      `/api/icc/admin/courses/${courseSlug}/modules/${moduleSlug}/quiz`,
      { method: 'PUT', body: JSON.stringify(data) },
    ),
  adminDeleteQuiz: (courseSlug: string, moduleSlug: string) =>
    req<unknown>(
      `/api/icc/admin/courses/${courseSlug}/modules/${moduleSlug}/quiz`,
      { method: 'DELETE' },
    ),
  adminExportCourse: (slug: string) =>
    req<unknown>(`/api/icc/admin/courses/${slug}/export`),
  adminImportCourse: (data: unknown, replaceModules = false) =>
    req<unknown>(
      `/api/icc/admin/courses/import?replaceModules=${replaceModules}`,
      { method: 'POST', body: JSON.stringify(data) },
    ),

  // ── Admin: Users & Subscriptions ──
  adminListUsers: (params: { email?: string; page?: number; size?: number } = {}) => {
    const q = new URLSearchParams()
    if (params.email) q.set('email', params.email)
    if (params.page !== undefined) q.set('page', String(params.page))
    if (params.size !== undefined) q.set('size', String(params.size))
    const qs = q.toString()
    return req<{ users: unknown[]; total: number; page: number; size: number }>(
      `/api/icc/admin/users${qs ? `?${qs}` : ''}`,
    )
  },
  adminGetUserDetail: (userId: string) =>
    req<unknown>(`/api/icc/admin/users/${userId}`),
  adminExtendSubscription: (userId: string, days: number, planSlug?: string) =>
    req<unknown>(`/api/icc/admin/users/${userId}/extend-subscription`, {
      method: 'POST',
      body: JSON.stringify({ days, planSlug }),
    }),
  adminCancelSubscription: (userId: string) =>
    req<unknown>(`/api/icc/admin/users/${userId}/cancel-subscription`, { method: 'POST' }),
}

export { clearAuth, PROJECT_KEY }
export default api
